import type { Run, RunEvent } from '@agents/shared';
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Icon, type IconName } from '../components/icons';
import { useCan } from '../components/shell';
import {
  AgentAvatar,
  Alert,
  BackLink,
  Button,
  ConfirmDialog,
  CopyButton,
  Disclosure,
  EmptyState,
  ErrorState,
  PageHeader,
  problemMessage,
  RunStatusPill,
  Segmented,
  SkeletonRows,
  TableSkeleton,
  useToast,
} from '../components/ui';
import { useDocumentTitle } from '../lib/document-title';
import { dateTime, duration, relativeTime, shortId, truncate } from '../lib/format';
import {
  isTerminal,
  useCancelRun,
  useCreateRun,
  useRun,
  useRunEvents,
  useRunStream,
  useRuns,
  useTools,
} from '../lib/queries';

const detailRoute = getRouteApi('/_app/ejecuciones/$runId');

type StatusGroup = 'all' | 'active' | 'completed' | 'failed';

const groupOf = (s: Run['status']): Exclude<StatusGroup, 'all'> | 'cancelled' =>
  s === 'queued' || s === 'running'
    ? 'active'
    : s === 'completed'
      ? 'completed'
      : s === 'cancelled'
        ? 'cancelled'
        : 'failed';

export function RunsPage() {
  useDocumentTitle('Ejecuciones');
  const runs = useRuns();
  const navigate = useNavigate();
  const [group, setGroup] = useState<StatusGroup>('all');
  const [agent, setAgent] = useState('all');

  const items = useMemo(() => runs.data ?? [], [runs.data]);
  const agents = useMemo(() => {
    const seen = new Map<string, string>();
    for (const r of items) seen.set(r.agentId, r.agentName);
    return [...seen].sort((a, b) => a[1].localeCompare(b[1], 'es'));
  }, [items]);
  const visible = items.filter(
    (r) => (group === 'all' || groupOf(r.status) === group) && (agent === 'all' || r.agentId === agent),
  );
  const count = (g: StatusGroup) => items.filter((r) => groupOf(r.status) === g).length;

  return (
    <>
      <PageHeader
        title="Ejecuciones"
        description="Las 50 ejecuciones más recientes de todos los agentes de este espacio."
      />
      {runs.isPending && <TableSkeleton rows={6} cols={5} />}
      {runs.isError && <ErrorState error={runs.error} onRetry={() => void runs.refetch()} />}
      {runs.data?.length === 0 && (
        <EmptyState
          icon="activity"
          title="Todavía no hay ejecuciones"
          action={
            <Link to="/agentes" className="btn">
              Ir a agentes
            </Link>
          }
        >
          Abre un agente publicado y pídele una tarea. Aquí verás su estado, su duración y su resultado.
        </EmptyState>
      )}
      {items.length > 0 && (
        <>
          <div className="toolbar">
            <Segmented<StatusGroup>
              label="Filtrar por estado"
              value={group}
              onChange={setGroup}
              options={[
                { value: 'all', label: 'Todas', count: items.length },
                { value: 'active', label: 'En curso', count: count('active') },
                { value: 'completed', label: 'Completadas', count: count('completed') },
                { value: 'failed', label: 'Con error', count: count('failed') },
              ]}
            />
            {agents.length > 1 && (
              <select
                className="select"
                aria-label="Filtrar por agente"
                value={agent}
                onChange={(e) => setAgent(e.target.value)}
              >
                <option value="all">Todos los agentes</option>
                {agents.map(([id, name]) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </select>
            )}
            <span className="count">
              {visible.length === items.length
                ? `${items.length} en total`
                : `${visible.length} de ${items.length}`}
            </span>
          </div>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">Estado</th>
                  <th scope="col">Agente</th>
                  <th scope="col" data-optional>
                    Solicitud
                  </th>
                  <th scope="col" className="num" data-optional>
                    Duración
                  </th>
                  <th scope="col" data-optional>
                    Cuándo
                  </th>
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => (
                  <tr
                    key={r.id}
                    data-href
                    onClick={(e) => {
                      if ((e.target as HTMLElement).closest('a')) return;
                      void navigate({ to: '/ejecuciones/$runId', params: { runId: r.id } });
                    }}
                  >
                    <td>
                      <RunStatusPill status={r.status} />
                    </td>
                    <td>
                      <div className="cell-main">
                        <AgentAvatar id={r.agentId} name={r.agentName} />
                        <div>
                          <Link to="/ejecuciones/$runId" params={{ runId: r.id }}>
                            {r.agentName}
                          </Link>
                          <small className="mono">
                            v{r.agentVersion} · {shortId(r.id)}
                          </small>
                        </div>
                      </div>
                    </td>
                    <td className="muted" data-optional>
                      <span className="cell-clip">{truncate(r.input, 90)}</span>
                    </td>
                    <td className="num mono" data-optional>
                      {duration(r.durationMs)}
                    </td>
                    <td className="muted nowrap" data-optional title={dateTime(r.createdAt)}>
                      {relativeTime(r.createdAt)}
                    </td>
                  </tr>
                ))}
                {!visible.length && (
                  <tr className="no-results">
                    <td colSpan={5}>
                      No hay ejecuciones con estos filtros.{' '}
                      <Button
                        variant="link"
                        onClick={() => {
                          setGroup('all');
                          setAgent('all');
                        }}
                      >
                        Limpiar filtros
                      </Button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ Steps */

type EventStep = {
  kind: 'event';
  seq: number;
  tone?: 'success' | 'warning' | 'danger';
  icon: IconName;
  title: string;
  at: string;
};

type ToolStep = {
  kind: 'tool';
  seq: number;
  tool: string;
  input?: string;
  output?: string;
  error: boolean;
  pending: boolean;
  at: string;
};

export type Step = EventStep | ToolStep;

const asText = (v: unknown) =>
  v === undefined || v === null ? undefined : typeof v === 'string' ? v : JSON.stringify(v, null, 2);

const STATUS_STEP: Record<string, Pick<EventStep, 'tone' | 'icon' | 'title'>> = {
  queued: { icon: 'clock', title: 'En cola' },
  running: { icon: 'play', title: 'Empezó a trabajar' },
  completed: { icon: 'check', tone: 'success', title: 'Terminó' },
  failed: { icon: 'x', tone: 'danger', title: 'La ejecución falló' },
  timed_out: { icon: 'clock', tone: 'danger', title: 'Se agotó el tiempo' },
  cancelled: { icon: 'x', title: 'Se canceló' },
};

/** "Consultó 3 fragmentos de Políticas.pdf y Catálogo" — names the documents, never their text. */
export function retrievalStep(p: Record<string, unknown>): Pick<EventStep, 'tone' | 'icon' | 'title'> {
  if (p.error) return { icon: 'book', tone: 'warning', title: 'No se pudo consultar el conocimiento' };
  const docs = (Array.isArray(p.documents) ? p.documents : []) as { title?: string }[];
  if (!docs.length)
    return { icon: 'book', title: 'Consultó el conocimiento sin encontrar información relacionada' };
  const titles = [...new Set(docs.map((d) => String(d.title ?? 'documento')))];
  const names =
    titles.length <= 2
      ? titles.join(' y ')
      : `${titles.slice(0, 2).join(', ')} y ${titles.length - 2 === 1 ? 'otro' : `${titles.length - 2} más`}`;
  const fragments = docs.length === 1 ? '1 fragmento' : `${docs.length} fragmentos`;
  return { icon: 'book', title: `Consultó ${fragments} de ${names}` };
}

/** Pairs each tool call with its result so they read as one card (Doc. 03/03 §11, Tool Call Card). */
export function buildSteps(events: RunEvent[]): Step[] {
  const steps: Step[] = [];
  for (const e of events) {
    const p = e.payload;
    switch (e.type) {
      case 'message_delta':
        break;
      case 'status': {
        const s = STATUS_STEP[String(p.status)];
        if (s) steps.push({ kind: 'event', seq: e.seq, at: e.createdAt, ...s });
        break;
      }
      case 'tool_call':
        steps.push({
          kind: 'tool',
          seq: e.seq,
          at: e.createdAt,
          tool: String(p.tool),
          input: asText(p.input),
          error: false,
          pending: true,
        });
        break;
      case 'tool_result': {
        const result = { output: asText(p.content), error: p.status === 'error', pending: false };
        const open = steps.findLast(
          (s): s is ToolStep => s.kind === 'tool' && s.pending && s.tool === String(p.tool),
        );
        if (open) Object.assign(open, result);
        else steps.push({ kind: 'tool', seq: e.seq, at: e.createdAt, tool: String(p.tool), ...result });
        break;
      }
      case 'message':
        steps.push({
          kind: 'event',
          seq: e.seq,
          at: e.createdAt,
          icon: 'message',
          title: 'Respuesta generada',
        });
        break;
      case 'error':
        steps.push({
          kind: 'event',
          seq: e.seq,
          at: e.createdAt,
          icon: 'x',
          tone: 'danger',
          title: String(p.message ?? 'Error'),
        });
        break;
      case 'retrieval': {
        steps.push({ kind: 'event', seq: e.seq, at: e.createdAt, ...retrievalStep(p) });
        break;
      }
      case 'policy': {
        const total = Object.values((p.counts ?? {}) as Record<string, number>).reduce((a, b) => a + b, 0);
        const where = p.source === 'tool' ? ` del resultado de ${String(p.tool)}` : '';
        steps.push({
          kind: 'event',
          seq: e.seq,
          at: e.createdAt,
          icon: 'shield',
          tone: 'warning',
          title:
            total === 1
              ? `Se ocultó 1 dato sensible${where} antes de enviarlo al modelo`
              : `Se ocultaron ${total} datos sensibles${where} antes de enviarlos al modelo`,
        });
        break;
      }
    }
  }
  return steps;
}

const clock = (iso: string) => new Date(iso).toLocaleTimeString('es', { hour12: false });

function Steps({ steps, active }: { steps: Step[]; active: boolean }) {
  const tools = useTools();
  const names = new Map(tools.data?.map((t) => [t.id, t.name]));
  if (!steps.length && !active) {
    return (
      <p className="muted" style={{ margin: 0 }}>
        No hay actividad registrada.
      </p>
    );
  }
  return (
    <ol className="steps" aria-label="Actividad de la ejecución">
      {steps.map((s) =>
        s.kind === 'tool' ? (
          <li key={s.seq} className="step" data-tone={s.error ? 'danger' : undefined}>
            <span className="dot">
              <Icon name="wrench" />
            </span>
            <div className="tool-card" data-status={s.error ? 'error' : 'ok'}>
              <header>
                {s.error && <Icon name="xCircle" />}
                {s.pending ? 'Usando' : 'Usó'} {names.get(s.tool) ?? s.tool}
                <span className="chip">{s.tool}</span>
              </header>
              <div className="io">
                <div>
                  <span>Entrada</span>
                  <pre>{s.input ?? '—'}</pre>
                </div>
                <div>
                  <span>Resultado</span>
                  <pre>{s.pending ? 'Esperando respuesta…' : (s.output ?? '—')}</pre>
                </div>
              </div>
            </div>
            <time dateTime={s.at}>{clock(s.at)}</time>
          </li>
        ) : (
          <li key={s.seq} className="step" data-tone={s.tone}>
            <span className="dot">
              <Icon name={s.icon} />
            </span>
            <div className="step-body">{s.title}</div>
            <time dateTime={s.at}>{clock(s.at)}</time>
          </li>
        ),
      )}
      {active && (
        <li className="step">
          <span className="dot">
            <span className="spinner" aria-hidden="true" />
          </span>
          <div className="step-body muted">Trabajando…</div>
          <span />
        </li>
      )}
    </ol>
  );
}

/* ------------------------------------------------------------------ Detail */

const LONG_INPUT = 320;

export function RunDetailPage() {
  const { runId } = detailRoute.useParams();
  // Keyed so "Ejecutar de nuevo" (same route, new id) starts with fresh UI state.
  return <RunDetail key={runId} runId={runId} />;
}

function RunDetail({ runId }: { runId: string }) {
  const [streaming, setStreaming] = useState(false);
  const run = useRun(runId, { poll: !streaming });
  const active = !!run.data && !isTerminal(run.data.status);
  useRunStream(runId, active, setStreaming);
  const events = useRunEvents(runId, active && !streaming);
  const liveText = (events.data ?? [])
    .filter((e) => e.type === 'message_delta')
    .map((e) => String(e.payload.text ?? ''))
    .join('');
  const cancel = useCancelRun(runId);
  const rerun = useCreateRun(run.data?.agentId ?? '');
  const navigate = useNavigate();
  const toast = useToast();
  const can = useCan();
  const [confirm, setConfirm] = useState(false);
  const [showFullInput, setShowFullInput] = useState(false);

  // Keep the header in step with the timeline when a status event arrives between polls.
  const lastStatus = events.data?.findLast((e) => e.type === 'status')?.payload.status;
  const { refetch } = run;
  const runStatus = run.data?.status;
  useEffect(() => {
    if (lastStatus && runStatus && lastStatus !== runStatus) void refetch();
  }, [lastStatus, runStatus, refetch]);

  useDocumentTitle(run.data ? `${run.data.agentName} · ${shortId(runId)}` : 'Ejecución');

  const back = <BackLink to="/ejecuciones">Ejecuciones</BackLink>;
  if (run.isPending) {
    return (
      <>
        {back}
        <div className="skeleton" style={{ width: 260, height: 28, marginBottom: 28 }} />
        <SkeletonRows rows={6} />
      </>
    );
  }
  if (run.isError) {
    return (
      <>
        {back}
        <ErrorState error={run.error} onRetry={() => void run.refetch()} />
      </>
    );
  }
  const r = run.data;
  const failed = r.status === 'failed' || r.status === 'timed_out';
  const steps = buildSteps(events.data ?? []);

  const doCancel = async () => {
    try {
      await cancel.mutateAsync();
      toast('Ejecución cancelada');
    } catch (err) {
      toast('No se pudo cancelar', { tone: 'danger', detail: problemMessage(err, 'Inténtalo de nuevo.') });
    } finally {
      setConfirm(false);
    }
  };

  const doRerun = async () => {
    try {
      const next = await rerun.mutateAsync(r.input);
      await navigate({ to: '/ejecuciones/$runId', params: { runId: next.id } });
    } catch (err) {
      toast('No se pudo iniciar', { tone: 'danger', detail: problemMessage(err, 'Inténtalo de nuevo.') });
    }
  };

  const rerunButton = can('run.create') && (
    <Button variant="primary" icon="rotate" onClick={() => void doRerun()} loading={rerun.isPending}>
      Ejecutar de nuevo
    </Button>
  );

  // One primary action per view: rerun lives in the error alert when the run failed.
  let headerActions: ReactNode;
  if (active) {
    headerActions = can('run.cancel') && (
      <Button variant="danger" onClick={() => setConfirm(true)}>
        Cancelar ejecución
      </Button>
    );
  } else if (failed) {
    headerActions = (
      <Link to="/agentes/$agentId" params={{ agentId: r.agentId }} className="btn">
        Ver agente
      </Link>
    );
  } else {
    headerActions = rerunButton;
  }

  const long = r.input.length > LONG_INPUT;

  return (
    <>
      <PageHeader
        back={back}
        lead={<AgentAvatar id={r.agentId} name={r.agentName} size="lg" />}
        title={r.agentName}
        meta={
          <>
            <RunStatusPill status={r.status} />
            {active ? (
              <span>Inició {relativeTime(r.startedAt ?? r.createdAt)}</span>
            ) : (
              <>
                <span title={dateTime(r.completedAt)}>Terminó {relativeTime(r.completedAt)}</span>
                <span className="sep" />
                <span className="mono">{duration(r.durationMs)}</span>
              </>
            )}
            <span className="sep" />
            <span className="mono">v{r.agentVersion}</span>
          </>
        }
        actions={headerActions}
      />

      <div className="split">
        <div className="stack" style={{ gap: 20 }}>
          <section className="request" aria-label="Solicitud">
            <div className="label">
              <span>Solicitud</span>
              <span className="mono" title={dateTime(r.createdAt)}>
                {relativeTime(r.createdAt)}
              </span>
            </div>
            <p className="prose" data-clamped={(long && !showFullInput) || undefined}>
              {r.input}
            </p>
            {long && (
              <Button
                variant="link"
                style={{ fontSize: 13, marginTop: 6 }}
                onClick={() => setShowFullInput((v) => !v)}
              >
                {showFullInput ? 'Ver menos' : 'Ver completa'}
              </Button>
            )}
          </section>

          {r.status === 'completed' && (
            <section className="result" aria-live="polite">
              <div className="result-head">
                <h2>
                  <Icon name="checkCircle" />
                  Resultado
                </h2>
                <CopyButton text={r.output ?? ''} />
              </div>
              <div className="result-body">
                <p className="prose">{r.output}</p>
              </div>
            </section>
          )}

          {active && (
            <section className="result" data-state="running" aria-live="polite" aria-busy="true">
              <div className="result-head">
                <h2>
                  <span className="spinner" aria-hidden="true" />
                  {r.status === 'queued' ? 'Esperando un trabajador disponible' : 'El agente está trabajando'}
                </h2>
              </div>
              <div className="result-body">
                {liveText ? (
                  <p className="prose">
                    {liveText}
                    <span className="caret" aria-hidden="true" />
                  </p>
                ) : (
                  <SkeletonRows rows={3} />
                )}
              </div>
            </section>
          )}

          {failed && (
            <Alert
              tone="danger"
              role="alert"
              title={
                r.errorMessage ??
                (r.status === 'timed_out'
                  ? 'Se agotó el tiempo de la ejecución'
                  : 'La ejecución no terminó correctamente')
              }
              actions={rerunButton}
            >
              <p>
                Puedes ejecutarla de nuevo. Si el error persiste, comparte este código con soporte:{' '}
                <span className="mono">{r.errorCode ?? 'desconocido'}</span>
              </p>
            </Alert>
          )}

          {r.status === 'cancelled' && (
            <Alert title="La ejecución se canceló antes de terminar">
              <p>Las acciones que ya había ejecutado no se revierten.</p>
            </Alert>
          )}

          <Disclosure
            title="Cómo llegó a este resultado"
            meta={`${steps.length} ${steps.length === 1 ? 'paso' : 'pasos'}${active ? ' · en curso' : ''}`}
            defaultOpen={active || failed}
          >
            {events.isPending && <SkeletonRows rows={2} />}
            {events.isError && <ErrorState error={events.error} onRetry={() => void events.refetch()} />}
            {events.data && <Steps steps={steps} active={active} />}
          </Disclosure>
        </div>

        <aside className="aside">
          <section className="aside-block">
            <h2>Detalles</h2>
            <dl className="facts">
              <dt>Agente</dt>
              <dd>
                <Link to="/agentes/$agentId" params={{ agentId: r.agentId }} className="btn-link">
                  {r.agentName}
                </Link>
              </dd>
              <dt>Versión</dt>
              <dd className="mono">v{r.agentVersion}</dd>
              <dt>Creada</dt>
              <dd>{dateTime(r.createdAt)}</dd>
              <dt>Inició</dt>
              <dd>{dateTime(r.startedAt)}</dd>
              <dt>Terminó</dt>
              <dd>{dateTime(r.completedAt)}</dd>
              <dt>Duración</dt>
              <dd className="mono">{duration(r.durationMs)}</dd>
              <dt>Herramientas</dt>
              <dd>
                {steps.filter((s) => s.kind === 'tool').length}{' '}
                {steps.filter((s) => s.kind === 'tool').length === 1 ? 'llamada' : 'llamadas'}
              </dd>
              <dt>Identificador</dt>
              <dd className="mono muted">{r.id}</dd>
            </dl>
          </section>
        </aside>
      </div>

      <ConfirmDialog
        open={confirm}
        onOpenChange={setConfirm}
        title="¿Cancelar esta ejecución?"
        description={`La ejecución de ${r.agentName} se detendrá en el siguiente paso. Las acciones que ya ejecutó no se revierten.`}
        cancelLabel="Seguir ejecutando"
        confirmLabel="Cancelar ejecución"
        onConfirm={() => void doCancel()}
        loading={cancel.isPending}
      />
    </>
  );
}
